// Executing a COMMITTED plan directory (`plan execute`) — the identity and the
// path grammar both transports and the worker lane share.
//
// A plan directory is the `/planx` convention every onboarded repository is
// seeded with (`apps/runner/src/modes/setup/base/claude-dir.ts`
// `renderPlanxCommand`): `docs/plans/<YYYY>/<MM>/<DD>/<NNN-slug>/` holding
// `overview.md`, one `<NN>-<aspect>.md` per slice and a `status.yml`. Executing
// one turns it into ONE plan — `plans` + `plan_groups`, the object `plan get`
// projects — whose groups release on their upstreams MERGING, through the gate
// that already exists (`upstreamGroupUnmergedSql`). Nothing here is a second
// scheduler; this file only names the plan and refuses a path that is not one.
//
// Namespace import: `@developerz/domain` is evaluated by the browser dashboard,
// where `node:crypto` is a stub that throws on property access; the id is only
// ever COMPUTED server-side (the `authored-task-id.ts` posture).
import * as nodeCrypto from 'node:crypto';
import { z } from 'zod';

/** The `plans.id` prefix of an executed plan directory — see {@link planExecutionId}. */
export const PLAN_EXECUTION_ID_PREFIX = 'pex_';

/**
 * The longest plan path accepted. 192, because the path is written INTO two bounded
 * envelope fields: every unit's `forbidden` fence (`forbidden[]` items cap at 300, the
 * fence sentence around the path is 108) and the status unit's `files_touched` entry
 * (`<path>/status.yml`, capped at 300). A longer path would refuse every unit at the
 * envelope parse instead of here, naming the path.
 */
export const PLAN_EXECUTION_PATH_MAX = 192;

/** The longest caller `revision` accepted. A label, not a document. */
export const PLAN_EXECUTION_REVISION_MAX = 64;

/**
 * The plan id of `(account, repo, path, revision)` — `pex_<sha256[..32]>`.
 *
 * DETERMINISTIC, and that is the idempotency of the whole verb: a retried
 * `plan execute` names the plan the first call wrote, the lane's
 * `insertPlanGroups` conflicts on it, and `authoredTaskId(planId, groupId)`
 * mints the same task ids on a re-approve. One plan directory is one plan.
 *
 * `revision` exists for the one case the path cannot carry: the plan's text was
 * corrected after a dry run and should be read AGAIN. A different revision is a
 * different plan; the caller cancels the stale one (`plan cancel`) so two plans
 * never execute one directory. Empty is the default revision.
 *
 * The account is IN the hash, so two tenants executing a same-named directory
 * never collide on `plans.id` (a primary key across all accounts).
 */
export function planExecutionId(input: {
  accountId: string;
  repoFullName: string;
  path: string;
  revision?: string;
}): string {
  const hex = nodeCrypto
    .createHash('sha256')
    .update(
      `plan_execute:${input.accountId}:${input.repoFullName}:${input.path}:${input.revision ?? ''}`,
      'utf8',
    )
    .digest('hex')
    .slice(0, 32);
  return `${PLAN_EXECUTION_ID_PREFIX}${hex}`;
}

/** Why a caller's plan path was refused — named, so the refusal can say which rule. */
export type PlanPathRefusal = 'empty' | 'too_long' | 'not_relative' | 'not_a_plain_path';

/**
 * Normalize a caller's plan directory to the repo-relative form every reader
 * compares on (no leading `./` or `/`-less prefix games, no trailing `/`), or
 * refuse it.
 *
 * STRICT on purpose: the path becomes a PREFIX FENCE (no unit may declare a file
 * under it) and a GitHub contents path. A glob, a `..` or a backslash would make
 * the fence compare against something the checkout does not contain, so the
 * units' file sets would silently include the plan's own documents — the exact
 * collision (every parallel PR editing `status.yml`) this verb exists to remove.
 */
export function normalizePlanPath(
  raw: string,
): { ok: true; path: string } | { ok: false; refusal: PlanPathRefusal } {
  let path = raw.trim();
  while (path.startsWith('./')) path = path.slice(2);
  while (path.endsWith('/')) path = path.slice(0, -1);
  if (path.length === 0) return { ok: false, refusal: 'empty' };
  if (path.length > PLAN_EXECUTION_PATH_MAX) return { ok: false, refusal: 'too_long' };
  if (path.startsWith('/') || path.startsWith('-')) return { ok: false, refusal: 'not_relative' };
  // biome-ignore lint/suspicious/noControlCharactersInRegex: refusing them is the point.
  if (/[\\*?[\]{}\x00-\x1f\x7f]/.test(path)) return { ok: false, refusal: 'not_a_plain_path' };
  if (path.split('/').some((s) => s === '' || s === '.' || s === '..')) {
    return { ok: false, refusal: 'not_a_plain_path' };
  }
  return { ok: true, path };
}

/**
 * The group id of the platform's own commit of a STORED plan's documents
 * (#5126). Reserved: a manifest unit may never carry it. It lives HERE, not
 * beside `PLAN_STATUS_GROUP_ID` in `packages/agent`, because `packages/db`
 * reads it too (`plan-groups/approve.ts`, `plan-groups/plan-files.ts`) and
 * depends only on `@developerz/domain` and `@developerz/kit`.
 */
export const PLAN_FILES_GROUP_ID = 'plan-files';

/**
 * THE GRAMMAR of a plan-files group id (#5128), one source for both worlds.
 * The base literal, or a revision-minted `<base>~<hex>` — exactly the 12
 * lowercase hex digits `revisedGroupId` (`plan-revision.ts`) mints.
 * `isPlanFilesGroupId` compiles this; SQL readers bind it as a Postgres `~`
 * regex parameter (`listUnlinkedPlanFilesGroups`,
 * `plan-groups/plan-files.ts`), so a row the TS predicate accepts is the same
 * row the SQL reader lists. The pattern carries its own anchors because a
 * Postgres regex does not anchor by default.
 */
export const PLAN_FILES_GROUP_ID_PATTERN = '^plan-files(~[0-9a-f]{12})?$';

const PLAN_FILES_GROUP_ID_RE = new RegExp(PLAN_FILES_GROUP_ID_PATTERN);

/**
 * THE ONE SEAM that decides "this group is the platform's documents commit"
 * (#5126, widened by #5128). Every reader — the adjudicator's reserved-id
 * check, `plan approve`'s no-task special case, the revision decision's own
 * plan-files exclusions (`revise-decide.ts`) — calls this rather than
 * comparing strings, so one body decides the base id and every revision id it
 * mints. A SQL reader cannot call it: the unlinked list
 * (`listUnlinkedPlanFilesGroups`, `plan-groups/plan-files.ts`) binds
 * `PLAN_FILES_GROUP_ID_PATTERN` as a `~` parameter instead — the same grammar
 * from the same source. `linkPlanFilesAnchor` and `deferPlanFilesGroup` (both
 * `plan-groups/plan-files.ts`) narrower still (#5130): each fences on the
 * EXACT (`plan_id`, `group_id`) row the lane listed, not on equality to the
 * base id — a kept, merged `plan-files` and a new `plan-files~<hex>` can
 * coexist on one plan after a revision, so "the plan's plan-files group" no
 * longer names one row.
 */
export function isPlanFilesGroupId(groupId: string): boolean {
  return PLAN_FILES_GROUP_ID_RE.test(groupId);
}

/**
 * Whether `candidate` (a repo-relative path, a directory when it ends in `/`)
 * lies INSIDE the plan directory `planPath` or IS it.
 *
 * Segment-prefix, the comparison every declared-path reader uses
 * (`provablyDisjointSql`, `pathsOverlap`): `docs/plans/x` does not contain
 * `docs/plans/xy/a.md`. A PARENT of the plan directory (`docs/`) is NOT inside
 * it — that declaration also covers other files and is the unit's own to make.
 */
export function isInsidePlanDir(planPath: string, candidate: string): boolean {
  const c = candidate.endsWith('/') ? candidate.slice(0, -1) : candidate;
  return c === planPath || c.startsWith(`${planPath}/`);
}

/**
 * The shape of a plan's stored documents: an array of `{ name, content }`,
 * parsed at the read seam so a malformed column THROWS at the read (#5106).
 *
 * The name is a plain FILE NAME — no `/`, no `\` — because the committed
 * reader only ever reads the directory's own top-level files
 * (`readCommittedPlanDocuments` in `apps/worker/src/consumers/plan-execute.ts`). A path separator in a name would either be a
 * traversal past the plan directory (the fence's own refusal) or a nested
 * document the platform never surfaces (a silent miss). Refusing both keeps
 * one rule over both readers.
 *
 * `content` is the bytes as a UTF-8 string. The committed reader base64-decodes
 * it; the stored reader holds it verbatim. A superRefine rejects duplicate
 * names — two slices in one directory must be two distinct files.
 */
export const PlanDocumentFilesSchema = z
  .array(
    z.object({
      name: z
        .string()
        .min(1)
        .refine((n) => !n.includes('/') && !n.includes('\\'), {
          message: 'name must not contain a path separator',
        }),
      content: z.string(),
    }),
  )
  .superRefine((arr, ctx) => {
    const seen = new Set<string>();
    for (let i = 0; i < arr.length; i += 1) {
      const n = arr[i]?.name;
      if (n === undefined) continue;
      if (seen.has(n)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'name'],
          message: `duplicate name ${JSON.stringify(n)}`,
        });
      } else {
        seen.add(n);
      }
    }
  });

/** One row of `PlanDocumentFilesSchema` — the door's writer shape (#5129). */
export type PlanDocumentFile = z.infer<typeof PlanDocumentFilesSchema>[number];
